import { type FormEvent, useState } from "react";
import { getAuth, sendEmailVerification, sendPasswordResetEmail, updateProfile, type User } from "firebase/auth";
import { Navigate, useNavigate } from "react-router-dom";
import { ShieldCheckIcon } from "@/components/icons";
import { useAuth } from "@/src/auth-context";
import { firebaseApp } from "@/src/firebase";
import { useDocumentMeta } from "@/src/use-document-meta";

export function SettingsPage() {
  const { user, loading } = useAuth();

  useDocumentMeta({
    title: "Account settings | Chuno",
    description: "Manage your Chuno account settings.",
    robots: "noindex,nofollow",
    canonical: "https://www.chuno.work/settings",
  });

  if (loading) return <main id="main" className="page-loading"><div><span />Loading settings…</div></main>;
  if (!user) return <Navigate to="/login?return_to=%2Fsettings" replace />;
  return <SettingsForm key={user.uid} user={user} />;
}

function SettingsForm({ user }: { user: User }) {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState(user.displayName || "");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const usesPassword = user.providerData.some((provider) => provider.providerId === "password");

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      await updateProfile(user, { displayName: displayName.trim() || null });
      setMessage("Profile updated.");
    } catch {
      setMessage("We couldn’t update your profile. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main id="main" className="account-page">
      <div className="shell settings-shell">
        <header className="settings-header"><p className="eyebrow"><span /> Account settings</p><h1>Your Chuno account.</h1><p>Manage your identity and sign-in options.</p></header>
        <div className="settings-grid">
          <section className="settings-card"><h2>Profile</h2><form className="settings-form" onSubmit={save}><label><span>Display name</span><input value={displayName} onChange={(event) => setDisplayName(event.target.value)} maxLength={80} placeholder="Your name" /></label><label><span>Email address</span><input value={user.email || ""} readOnly disabled /></label>{message ? <p className="settings-message" role="status">{message}</p> : null}<button className="button button--dark" type="submit" disabled={busy}>{busy ? "Saving…" : "Save profile"}</button></form></section>
          <section className="settings-card"><h2>Sign-in & security</h2><div className="security-row"><ShieldCheckIcon /><div><strong>{user.emailVerified ? "Email verified" : "Email not verified"}</strong><p>{user.email}</p></div></div>{!user.emailVerified ? <button className="settings-action" type="button" onClick={() => { setMessage(""); void sendEmailVerification(user).then(() => setMessage("Verification email sent.")).catch(() => setMessage("Couldn’t send verification email.")); }}>Send verification email</button> : null}{usesPassword && user.email ? <button className="settings-action" type="button" onClick={() => { setMessage(""); void sendPasswordResetEmail(getAuth(firebaseApp), user.email!).then(() => setMessage("Password reset email sent.")).catch(() => setMessage("Couldn’t send password reset email.")); }}>Send password reset</button> : null}<button className="settings-action settings-action--danger" type="button" onClick={() => void signOut().then(() => navigate("/", { replace: true }))}>Sign out</button></section>
        </div>
      </div>
    </main>
  );
}
